import { db } from "./db";
import { ingestEvents, type IngestEvent } from "./api";
import type { QueuedEvent } from "./types";

const MAX_EVENTS_PER_REQUEST = 50;
const MAX_EVENTS_PER_FLUSH = 250;
const FLUSH_DELAY_MS = 2000;

let initialized = false;
let flushTimer: number | null = null;
let flushing = false;

const seqCache = new Map<string, number>();

const nowMs = () => Date.now();

const apiUrl = (path: string) => `${import.meta.env.VITE_API_ENDPOINT}${path}`;

const backoffMs = (attempts: number) => {
    const base = 2000;
    const max = 60_000;
    const ms = base * Math.pow(2, Math.max(0, attempts - 1));
    return Math.min(max, ms);
};

const generateId = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return crypto.randomUUID();
    }
    return `${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`;
};

const seqStorageKey = (session: string) => `twokings_seq_${session}`;

const getNextSeq = async (session: string): Promise<number> => {
    const cached = seqCache.get(session);
    if (cached !== undefined) {
        seqCache.set(session, cached + 1);
        if (typeof localStorage !== "undefined") {
            localStorage.setItem(seqStorageKey(session), String(cached + 1));
        }
        return cached;
    }

    let start = 0;

    if (typeof localStorage !== "undefined") {
        const raw = localStorage.getItem(seqStorageKey(session));
        if (raw !== null) {
            const parsed = parseInt(raw, 10);
            if (!Number.isNaN(parsed) && parsed >= 0) {
                start = parsed;
            }
        }
    }

    if (start === 0) {
        const last = await db.events
            .where("[session+seq]")
            .between([session, 0], [session, Number.MAX_SAFE_INTEGER])
            .last();
        if (last && typeof last.seq === "number" && last.seq >= 0) {
            start = last.seq + 1;
        }
    }

    seqCache.set(session, start + 1);
    if (typeof localStorage !== "undefined") {
        localStorage.setItem(seqStorageKey(session), String(start + 1));
    }
    return start;
};

const scheduleFlush = () => {
    if (typeof window === "undefined") return;
    if (flushTimer !== null) return;

    flushTimer = window.setTimeout(() => {
        flushTimer = null;
        void flush().catch(() => {});
    }, FLUSH_DELAY_MS);
};

const ensureInitialized = () => {
    if (initialized) return;
    initialized = true;

    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                void flushWithBeacon();
            }
        });
    }

    if (typeof window !== "undefined") {
        window.addEventListener("beforeunload", () => {
            void flushWithBeacon();
        });
    }
};

export const initEventLogging = () => {
    ensureInitialized();
    scheduleFlush();
};

const toIngestEvent = (e: QueuedEvent): IngestEvent => {
    return {
        client_event_id: e.client_event_id,
        session: e.session,
        seq: e.seq,
        event_type: e.event_type,
        elapsed_ms: e.elapsed_ms,
        client_ts: e.client_ts,
        data: e.data,
    };
};

export const logEvent = async (input: {
    session: string;
    event_type: string;
    elapsed_ms?: number;
    data?: unknown;
}) => {
    try {
        ensureInitialized();

        const session = input.session;
        if (!session) return;

        const seq = await getNextSeq(session);
        const t = nowMs();

        const event: QueuedEvent = {
            client_event_id: generateId(),
            session,
            seq,
            event_type: input.event_type,
            elapsed_ms: input.elapsed_ms,
            client_ts: t,
            data: input.data,
            created_at: t,
            next_retry_at: t,
            attempts: 0,
        };

        await db.events.add(event);
        scheduleFlush();
    } catch {
    }
};

export const flush = async () => {
    if (flushing) return;
    flushing = true;

    try {
        let flushed = 0;

        while (flushed < MAX_EVENTS_PER_FLUSH) {
            const now = nowMs();
            const batch = await db.events
                .where("next_retry_at")
                .belowOrEqual(now)
                .limit(MAX_EVENTS_PER_REQUEST)
                .toArray();

            if (batch.length === 0) return;

            try {
                await ingestEvents(batch.map(toIngestEvent));
                const ids = batch.map((e) => e.id).filter((v): v is number => v !== undefined);
                if (ids.length > 0) {
                    await db.events.bulkDelete(ids);
                }
            } catch {
                const retryAt = nowMs();
                const updated = batch.map((e) => ({
                    ...e,
                    attempts: (e.attempts ?? 0) + 1,
                    next_retry_at: retryAt + backoffMs((e.attempts ?? 0) + 1),
                }));
                await db.events.bulkPut(updated);
                return;
            }

            flushed += batch.length;
        }
    } catch {
        return;
    } finally {
        flushing = false;
        scheduleFlush();
    }
};

export const flushWithBeacon = async () => {
    try {
        if (typeof navigator === "undefined") return;
        const now = nowMs();
        const batch = await db.events.where("next_retry_at").belowOrEqual(now).limit(20).toArray();
        if (batch.length === 0) return;

        const payload = JSON.stringify({ events: batch.map(toIngestEvent) });
        let sent = false;

        if (typeof navigator.sendBeacon === "function") {
            sent = navigator.sendBeacon(
                apiUrl("/events"),
                new Blob([payload], { type: "application/json" }),
            );
        } else {
            try {
                await fetch(apiUrl("/events"), {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    mode: "cors",
                    body: payload,
                    keepalive: true,
                });
                sent = true;
            } catch {
                sent = false;
            }
        }

        if (!sent) return;
        const updated = batch.map((e) => ({
            ...e,
            attempts: (e.attempts ?? 0) + 1,
            next_retry_at: now + backoffMs((e.attempts ?? 0) + 1),
        }));
        await db.events.bulkPut(updated);
    } catch {
    }
};
